import { useRangeCalendarState as originalUseRangeCalendarState } from '@react-stately/calendar';
import {
  RangeCalendarStateOptions as OriginalRangeCalendarStateOptions,
  RangeCalendarState as OriginalRangeCalendarState,
} from '@react-stately/calendar';
import { DateValue } from '@internationalized/date';
import { useRef } from 'react';
import { RangeCalendarStateKeys, ValueBasePropsKeys } from '../../utils/types';

type OriginalOnChange<T extends DateValue> = NonNullable<
  OriginalRangeCalendarStateOptions<T>['onChange']
>;

export type RangeCalendarStateOptions<
  T extends DateValue,
  P extends string
> = Pick<
  OriginalRangeCalendarStateOptions<T>,
  | ValueBasePropsKeys
  | 'locale'
  | 'createCalendar'
  | 'defaultFocusedValue'
  | 'allowsNonContiguousRanges'
  | 'visibleDuration'
> & {
  onChange?: (
    value: Parameters<OriginalOnChange<T>>[0],
    predefinedRange?: P
  ) => ReturnType<OriginalOnChange<T>>;
};

type OriginalSetValue = OriginalRangeCalendarState['setValue'];

export type RangeCalendarState<P extends string> = Pick<
  OriginalRangeCalendarState,
  RangeCalendarStateKeys
> & {
  setValue: (
    value: Parameters<OriginalSetValue>[0],
    predefinedRange?: P
  ) => ReturnType<OriginalSetValue>;
};

/**
 * A wrapper to allow for the onChange callback to have extra info
 **/
export const useRangeCalendarState = <P extends string>({
  onChange,
  ...options
}: RangeCalendarStateOptions<DateValue, P>): RangeCalendarState<P> => {
  const lastPredefinedRange = useRef<P | undefined>(undefined);

  const state = originalUseRangeCalendarState({
    ...options,
    onChange: (value) => {
      onChange?.(value, lastPredefinedRange.current);
      lastPredefinedRange.current = undefined;
    },
  });

  return {
    ...state,
    setValue: (value, predefinedRange) => {
      lastPredefinedRange.current = predefinedRange;
      state.setValue(value);
    },
  };
};
